import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { AuthProvider } from "@/auth/AuthProvider";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { SiteLayout } from "@/components/SiteLayout";
import { AdminLayout } from "@/components/AdminLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useSketchErrorLogging } from "@/components/SketchFrame";
import { isProtected, routes } from "@/routes";
import Login from "@/pages/Login";
import NotFound from "@/pages/NotFound";
import Forbidden from "@/pages/Forbidden";

/** Wrap a registry entry in its guard, if it declares one. */
function render(route: (typeof routes)[number]) {
  const Element = route.element;
  if (!isProtected(route)) return <Element />;
  return (
    <ProtectedRoute permissions={route.permissions} masterOnly={route.masterOnly}>
      <Element />
    </ProtectedRoute>
  );
}

export default function App() {
  // Sketch frames are cross-origin; this relays their errors to the console.
  useSketchErrorLogging();

  const siteRoutes = routes.filter((r) => r.area !== "admin");
  const adminRoutes = routes.filter((r) => r.area === "admin");

  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <Routes>
            {/* Auth screens sit outside both shells. */}
            <Route path="/login" element={<Login />} />

            {/* Admin gets its own sidebar shell. */}
            <Route element={<AdminLayout />}>
              {adminRoutes.map((route) => (
                <Route key={route.path} path={route.path} element={render(route)} />
              ))}
            </Route>

            {/* Everything else uses the public shell. */}
            <Route element={<SiteLayout />}>
              {siteRoutes.map((route) => (
                <Route key={route.path} path={route.path} element={render(route)} />
              ))}
              <Route path="/403" element={<Forbidden />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Route>
          </Routes>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}
